import { useCallback } from 'react';
import dayjs from 'dayjs';
import { notification } from 'antd';
import { createApiGateway } from '../../../../apiGateway';
import { type UsersyncStatisticsFilters, type UsersyncStatisticsParams } from '../../../../types/usersyncStatistics';

const apiGateway = createApiGateway();

const buildFilename = (filters: UsersyncStatisticsFilters): string => {
  const from = filters.dateRange[0];
  const to = filters.dateRange[1];
  const format = filters.format;
  const groupBy = filters.groupBy && filters.groupBy.length > 0 ? filters.groupBy.join(',') : 'none';
  return `usersync_statistics_${from}_to_${to}_${format}_${groupBy}`.replace(/[^a-zA-Z0-9_\-.,]/g, '_') + '.csv';
};

type ExportCsvArgs = {
  filters: UsersyncStatisticsFilters;
  order: {
    by?: string;
    direction?: 'ASC' | 'DESC';
  };
  pagination: {
    current: number;
    pageSize: number;
  };
  shouldPaginate: boolean;
};

export const useExportUsersyncStatisticsCsv = () => {
  const exportCsv = useCallback(async ({ filters, order, pagination, shouldPaginate }: ExportCsvArgs) => {
    const params: UsersyncStatisticsParams = {
      from: filters.dateRange[0],
      to: dayjs(filters.dateRange[1]).add(1, 'day').format('YYYY-MM-DD'),
    };

    if (filters.format !== 'summary') {
      params.format = filters.format;
    }

    if (filters.groupBy && filters.groupBy.length > 0) {
      params.groupBy = filters.groupBy.join(',');
    }

    (['sspId', 'dspId', 'stage', 'status', 'reason', 'tagType'] as const).forEach((field) => {
      const value = filters[field];
      if (value && value.length > 0) {
        params[field] = value.join(',');
      }
    });

    if (order.by) {
      params.orderBy = order.by;
    }

    if (order.direction) {
      params.orderDirection = order.direction;
    }

    if (shouldPaginate) {
      params.limit = pagination.pageSize;
      params.offset = (pagination.current - 1) * pagination.pageSize;
    }

    try {
      const { blob, filename } = await apiGateway.usersyncStatistics.exportUsersyncStatistics(params);
      const resolvedFilename = filename || buildFilename(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', resolvedFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export CSV. Please try again later.';
      notification.error({
        title: 'Export Failed',
        description: message,
      });
    }
  }, []);

  return { exportCsv };
};
